import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Components
import {
  CustomButton,
  CustomInput,
  CustomTitle,
  ShowMessage,
} from '../../components';
import { useSpin } from '../../components/SpinContext';
import { Col, Flex, Form, message, Row } from 'antd';
import { CreatePasswordApi, GetEmailVerified } from '../../apis/auth';
// Assets
import CheckMarkIcon from '../../assets/images/check-mark.png';
import { getThemeAccentColor } from '@/utils/themeColor';

interface CreatePasswordProps {
  type?: 'create' | 'reset';
}

interface FormValues {
  password: string;
}

const CreatePassword: React.FC<CreatePasswordProps> = ({ type }) => {
  const { showSpin, hideSpin } = useSpin();
  const [form] = Form.useForm<FormValues>();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('token');
  const [isEmailSuccess, setIsEmailSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const themeColor = getThemeAccentColor();
  const btnColor = getThemeAccentColor();

  const onFinish = (values: FormValues): void => {
    if (id && values.password) {
      const model = {
        new_password: values.password,
        confirm_password: values.password,
        token: id,
      };
      showSpin();
      setIsFormValid(true);
      CreatePasswordApi(model)
        .then((res) => {
          if (res?.data?.data?.is_success) {
            localStorage.clear();
            navigate('/login');
          } else {
            message.error(res?.data?.data?.message);
          }
        })
        .catch(() => {})
        .finally(() => {
          hideSpin();
          setIsFormValid(false);
        });
    } else {
      message.error('Something went wrong');
    }
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        showSpin();
        const xyz = id + '/';
        const res = await GetEmailVerified(xyz);
        if (res.data.data?.is_success) {
          setIsEmailSuccess(true);
        } else {
          setIsEmailSuccess(false);
          setErrorMessage(res.data.detail);
        }
      } catch (err) {
        console.error(err);
      } finally {
        hideSpin();
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, hideSpin, showSpin]);

  return (
    <>
      <div className='zl-create-pass'>
        {isEmailSuccess ? (
          <Flex
            vertical={true}
            align='center'
            justify='center'
            style={{ height: 'inherit' }}
          >
            <ShowMessage
              heading='Email address verified'
              text={
                type === 'reset'
                  ? 'Reset password for your account to get started.'
                  : 'Create a password for your account to get started.'
              }
              icon={CheckMarkIcon}
              themeColor={themeColor}
            >
              <Flex justify='center'>
                <Form
                  form={form}
                  name='validateOnly'
                  onFinish={onFinish}
                  layout='vertical'
                  autoComplete='off'
                >
                  <Row gutter={[16, 24]}>
                    <Col span={24}>
                      <Form.Item
                        name='password'
                        rules={[
                          {
                            required: true,
                            message: 'Please create your password',
                          },
                          {
                            type: 'string',
                            min: 6,
                            message:
                              'Password must be at least 6 characters long.',
                          },
                          {
                            type: 'string',
                            max: 20,
                            message: 'Password cannot exceed 20 characters.',
                          },
                        ]}
                      >
                        <CustomInput
                          aria-label={
                            type === 'reset' ? 'New password' : 'Password'
                          }
                          type='password'
                          placeholder='Enter password'
                          textColor='var(--text-secondary)'
                          borderRadius='8px'
                          placeholderColor='var(--text-secondary)'
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item>
                        <CustomButton
                          text={
                            type === 'reset'
                              ? 'Reset Password'
                              : 'Create Password'
                          }
                          block={true}
                          height='48px'
                          bgColor={btnColor}
                          color='#fff'
                          borderRadius='8px'
                          fontSize='16'
                          fontWeight='500'
                          htmlType='submit'
                          isFormValid={isFormValid}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Flex>
            </ShowMessage>
          </Flex>
        ) : (
          <CustomTitle
            text={errorMessage}
            color='var(--text-primary)'
            fontSize='32px'
            lineHeight='3.782rem'
          />
        )}
      </div>
    </>
  );
};

export default CreatePassword;
